import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../store/store';
import { getUUID } from '../../../utils/UUID';
import { ReportingDetailsGoalsAddTemplate } from '../../templates';
import Spinner from '../../atoms/Spinner/Spinner';
import { GoalResponseDto } from '../../../store/landscapeAPI/goal/dto/response/goal-response.dto';
import { GoalType } from '../../../store/landscapeAPI/goal/goal-type';
import { GoalThemeSaveRequestDto } from '../../../store/landscapeAPI/goal/dto/request/goal-theme-save-request.dto';
import { GoalCollectionSaveRequestDto } from '../../../store/landscapeAPI/goal/dto/request/goal-collection-save-request.dto';
import { IndicatorGoalSaveRequestDto } from '../../../store/landscapeAPI/indicator/dto/request/indicator-goal-save-request.dto';
import { GoalSaveRequestDto } from '../../../store/landscapeAPI/goal/dto/request/goal-save-request.dto';
import { saveGoal } from '../../../store/landscapeAPI/goal/reducers/goal.slice';
import { createGoalTheme } from '../../../store/landscapeAPI/goal/reducers/goalTheme.slice';
import { createGoalCollection } from '../../../store/landscapeAPI/goal/reducers/goalCollection.slice';
import { saveIndicatorGoal } from '../../../store/landscapeAPI/indicator/reducers/indicatorGoal.slice';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import styles from './ReportingDetailsGoalsAddPage.module.css';
import cn from 'classnames';
import { PromiseSettledResult } from '../../../utils/types/types';

interface ReportingDetailsGoalsAddPageProps {}

const ReportingDetailsGoalsAddPage: FC<
  ReportingDetailsGoalsAddPageProps
> = () => {
  const [goal, setGoal] = useState<GoalResponseDto>({
    name: '',
    indicators: [],
    type: GoalType.Core,
    themes: [],
    collections: [],
    id: getUUID(),
    versionId: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    language: '',
    published: true,
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleOnChange = (goal: GoalResponseDto) => {
    setGoal(goal);
  };

  const { status } = useSelector((state: RootState) => state.goalSlice);
  const { status: goalThemesStatus } = useSelector(
    (state: RootState) => state.goalThemeSlice
  );
  const { status: goalCollectionsStatus } = useSelector(
    (state: RootState) => state.goalCollectionSlice
  );
  const { status: goalIndicatorsStatus } = useSelector(
    (state: RootState) => state.indicatorGoalSlice
  );

  const [goalThemes, setGoalThemes] = useState<GoalThemeSaveRequestDto[]>([]);
  const handleOnGoalThemesChange = (goalThemes: GoalThemeSaveRequestDto[]) => {
    setGoalThemes(goalThemes);
  };

  const [goalCollections, setGoalCollections] = useState<
    GoalCollectionSaveRequestDto[]
  >([]);
  const handleOnGoalCollectionsChange = (
    goalCollections: GoalCollectionSaveRequestDto[]
  ) => {
    setGoalCollections(goalCollections);
  };

  const [goalIndicators, setGoalIndicators] = useState<
    IndicatorGoalSaveRequestDto[]
  >([]);
  const handleOnGoalIndicatorsChange = (
    goalIndicators: IndicatorGoalSaveRequestDto[]
  ) => {
    setGoalIndicators(goalIndicators);
  };

  const handleAddGoal = async (goal: GoalResponseDto) => {
    const data: GoalSaveRequestDto = {
      id: goal.id,
      name: goal.name,
      type: goal.type,
      published: goal.published,
    };

    const response = await dispatch(
      saveGoal({ saveRequest: data, language: selectedLanguage })
    );
    const newGoal = response.payload as GoalResponseDto;

    await Promise.all([
      ...goalThemes.map((goalTheme) => {
        return dispatch(createGoalTheme(goalTheme));
      }),

      ...goalCollections.map((goalCollection) => {
        return dispatch(createGoalCollection(goalCollection));
      }),

      ...goalIndicators.map((goalIndicator) => {
        return dispatch(saveIndicatorGoal(goalIndicator));
      }),
    ]);

    navigate(`/reporting-details/goals/${newGoal.id}/edit`);
  };

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations: () => Promise<
    PromiseSettledResult<void>[]
  > = async () => {
    const rejectedResults: PromiseSettledResult<void>[] = [
      {
        status: 'rejected',
        reason: new Error(
          'Must save verification status before generating translation'
        ),
      },
    ];

    return Promise.reject(rejectedResults);
  };

  const generateTranslation = async () => {};

  if (
    status === 'loading' ||
    goalThemesStatus === 'loading' ||
    goalCollectionsStatus === 'loading' ||
    goalIndicatorsStatus === 'loading'
  ) {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      <ReportingDetailsGoalsAddTemplate
        data={goal}
        goalThemes={[]}
        handleOnGoalThemesChange={handleOnGoalThemesChange}
        goalCollections={[]}
        handleOnGoalCollectionsChange={handleOnGoalCollectionsChange}
        goalIndicators={[]}
        handleOnGoalIndicatorsChange={handleOnGoalIndicatorsChange}
        handleAddGoal={handleAddGoal}
        handleOnChange={handleOnChange}
        setLanguage={setLanguage}
        selectedLanguage={selectedLanguage}
        fromLanguage={fromLanguage}
        setFromLanguage={setFromLanguage}
        toLanguage={toLanguage}
        setToLanguage={setToLanguage}
        generateTranslation={generateTranslation}
        generateAllTranslations={generateAllTranslations}
      />
    </div>
  );
};

export default ReportingDetailsGoalsAddPage;
